/**
 * DashboardDomains — /website-builder/domains and /settings/domains
 *
 * Connect, verify, set-primary, and disconnect custom domains.
 *   GET    /api/builder/domains
 *   POST   /api/builder/domains              {domain}
 *   POST   /api/builder/domains/{id}/verify  → live DNS check
 *   POST   /api/builder/domains/{id}/primary
 *   DELETE /api/builder/domains/{id}
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Globe, Loader2, Check, AlertTriangle, Clock, Plus, RefreshCw,
  Copy, Shield, Trash2, ExternalLink, Star, X, Server, CheckCircle2, XCircle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';
import DomainWizardModal, { hasDomainWizardBeenSeen } from './domains/DomainWizardModal';
import { HelpCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_META = {
  pending:  { label: 'Pending DNS',   cls: 'bg-amber-500/15 text-amber-300 border-amber-400/25',   icon: Clock },
  verified: { label: 'Verified',      cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/25',       icon: Check },
  live:     { label: 'Live',          cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25', icon: Check },
  error:    { label: 'DNS Error',     cls: 'bg-red-500/15 text-red-300 border-red-400/25',          icon: AlertTriangle },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${m.cls}`} data-testid={`domain-status-${status}`}>
      <Icon size={10} /> {m.label}
    </span>
  );
}

function DnsRecordRow({ rec }) {
  const [copied, setCopied] = useState(null);
  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1200);
  };
  return (
    <div className="grid grid-cols-[70px_120px_1fr_28px] gap-2 items-center text-[12px] py-2 border-b border-white/[0.04] last:border-0" data-testid={`dns-record-${rec.type}`}>
      <span className="font-mono font-bold text-violet-300">{rec.type}</span>
      <span className="font-mono text-white/70 truncate">{rec.host}</span>
      <div className="flex items-center gap-2 min-w-0">
        <code className="font-mono text-white/90 bg-white/5 px-2 py-1 rounded truncate flex-1" title={rec.value}>{rec.value}</code>
        <button onClick={() => copy(rec.value, rec.type + rec.host)} className="p-1 rounded text-white/55 hover:text-white hover:bg-white/5">
          {copied === rec.type + rec.host ? <Check size={12} /> : <Copy size={12} />}
        </button>
      </div>
      <span className="text-white/35 text-[10.5px] font-mono text-right">{rec.ttl}s</span>
    </div>
  );
}

function AddDomainModal({ open, onClose, onAdded }) {
  const [domain, setDomain] = useState('');
  const [busy, setBusy]     = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    const d = domain.trim().toLowerCase().replace(/^https?:\/\//, '').split('/')[0];
    if (!d || !/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d)) { toast.error('Enter a valid domain like mystore.com'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/builder/domains`, { domain: d }, { headers: headers() });
      onAdded(r.data);
      toast.success('Domain added — add DNS records to verify');
      setDomain('');
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add failed'); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#15152E] border border-white/10 rounded-2xl p-6 w-full max-w-md" data-testid="domain-add-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] flex items-center justify-center"><Globe size={16} className="text-white" /></div>
            <h3 className="text-white font-bold font-syne text-lg">Connect a domain</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/5"><X size={16} /></button>
        </div>
        <Label className="text-[#A5A8C9] text-[11px]">Your domain *</Label>
        <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="mystore.com or shop.mystore.com" className="bg-white/5 border-white/10 text-white text-sm mt-1.5 rounded-xl" autoFocus data-testid="domain-add-input" />
        <p className="text-[11px] text-white/55 mt-2">You&apos;ll need access to your domain registrar&apos;s DNS settings (GoDaddy, Namecheap, Cloudflare, etc.) to add an A record + TXT record.</p>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-wider font-bold text-white/65 hover:text-white">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 flex items-center gap-2" data-testid="domain-add-submit">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add domain
          </button>
        </div>
      </form>
    </div>
  );
}

function VerifyResultRow({ ok, label, hint }) {
  return (
    <div className="flex items-start gap-2 text-[12px]">
      {ok ? <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /> : <XCircle size={14} className="text-red-400 mt-0.5 shrink-0" />}
      <div>
        <p className={ok ? 'text-emerald-200' : 'text-red-200'}>{label}</p>
        {hint && <p className="text-white/45 text-[11px] mt-0.5">{hint}</p>}
      </div>
    </div>
  );
}

function DomainCard({ d, onVerify, onPrimary, onDelete, verifying }) {
  const [expanded, setExpanded] = useState(d.status === 'pending');
  const check = d.last_check_result;
  return (
    <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl overflow-hidden" data-testid={`domain-card-${d.id}`}>
      <div className="px-5 py-4 flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-[#A78BFA]/10 flex items-center justify-center shrink-0">
          <Globe size={16} className="text-[#A78BFA]" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[14px] font-bold text-white font-syne">{d.host}</p>
            <StatusPill status={d.status} />
            {d.is_primary && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/25">
                <Star size={9} /> Primary
              </span>
            )}
            {d.status === 'verified' && d.ssl_status === 'provisioning' && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/8 text-white/65 border border-white/15">
                <Shield size={9} /> SSL provisioning…
              </span>
            )}
          </div>
          <p className="text-[11.5px] text-white/55 mt-1">
            {d.last_checked_at ? `Last checked ${new Date(d.last_checked_at).toLocaleString()}` : 'Not yet verified'}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {d.status === 'verified' && !d.is_primary && (
            <button onClick={() => onPrimary(d)} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-200 bg-amber-500/10 hover:bg-amber-500/15 rounded-md flex items-center gap-1.5" data-testid={`domain-primary-${d.id}`}>
              <Star size={11} /> Make primary
            </button>
          )}
          <button onClick={() => onVerify(d)} disabled={verifying === d.id} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:shadow-[0_8px_22px_rgba(139,92,246,0.4)] rounded-md flex items-center gap-1.5 disabled:opacity-50" data-testid={`domain-verify-${d.id}`}>
            {verifying === d.id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Verify
          </button>
          <a href={`https://${d.host}`} target="_blank" rel="noreferrer" className="p-2 rounded-md text-white/65 hover:text-white hover:bg-white/5" title="Visit site" data-testid={`domain-visit-${d.id}`}><ExternalLink size={14} /></a>
          <button onClick={() => onDelete(d)} className="p-2 rounded-md text-white/55 hover:text-red-400 hover:bg-red-500/10" data-testid={`domain-delete-${d.id}`}><Trash2 size={14} /></button>
        </div>
      </div>

      <button onClick={() => setExpanded((v) => !v)} className="w-full px-5 py-2.5 border-t border-white/[0.04] flex items-center justify-between text-[11px] uppercase tracking-wider font-bold text-violet-300 hover:bg-white/[0.02]" data-testid={`domain-expand-${d.id}`}>
        <span className="flex items-center gap-2"><Server size={12} /> DNS records & status</span>
        <span className="text-white/40">{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div className="px-5 py-4 bg-white/[0.02] border-t border-white/[0.04] space-y-4">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2">Add these records at your registrar</p>
            <div className="bg-[#11112A] rounded-xl px-4 py-2 border border-white/[0.04]">
              <div className="grid grid-cols-[70px_120px_1fr_28px] gap-2 text-[10px] uppercase tracking-wider font-bold text-white/35 pb-2 border-b border-white/[0.04]">
                <span>Type</span><span>Host / Name</span><span>Value</span><span className="text-right">TTL</span>
              </div>
              {(d.dns_records || []).map((rec, i) => <DnsRecordRow key={i} rec={rec} />)}
            </div>
          </div>

          {check && (
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2">Last verification check</p>
              <div className="bg-[#11112A] rounded-xl px-4 py-3 border border-white/[0.04] space-y-2">
                <VerifyResultRow
                  ok={check.routing?.pass}
                  label={check.routing?.pass ? 'Routing record found ✓' : 'Routing record not yet found'}
                  hint={!check.routing?.pass ? `Expected A=${check.routing?.expected_a} or CNAME=${check.routing?.expected_cname}. Found A=${(check.routing?.found_a || []).join(', ') || '(none)'}` : ''}
                />
                <VerifyResultRow
                  ok={check.ownership?.pass}
                  label={check.ownership?.pass ? 'Ownership TXT verified ✓' : 'Ownership TXT not yet found'}
                  hint={!check.ownership?.pass ? `Add TXT at ${check.ownership?.host_checked} containing the token above` : ''}
                />
                <p className="text-[10.5px] text-white/35 pt-1">DNS changes can take 5 min – 48 h to propagate. Click Verify again after a few minutes.</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DashboardDomains() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [openAdd, setOpenAdd]   = useState(false);
  const [openWizard, setOpenWizard] = useState(false);
  const [verifying, setVerify]  = useState(null);
  const [platform, setPlatform] = useState({ ip: '', cname: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/domains`, { headers: headers() });
      const list = r.data?.items || [];
      setItems(list);
      setPlatform({ ip: r.data?.platform_ip || '', cname: r.data?.platform_cname || '' });
      // Auto-open the education wizard on first visit when no domains connected
      if (list.length === 0 && !hasDomainWizardBeenSeen()) {
        setOpenWizard(true);
      }
    } catch { toast.error('Failed to load domains'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const verify = async (d) => {
    setVerify(d.id);
    try {
      const r = await axios.post(`${API}/builder/domains/${d.id}/verify`, {}, { headers: headers() });
      setItems((arr) => arr.map((x) => (x.id === d.id ? r.data : x)));
      if (r.data.status === 'verified') toast.success(`${d.host} verified! SSL is provisioning…`);
      else toast.error('DNS not ready yet — check the records below');
    } catch (e) { toast.error(e.response?.data?.detail || 'Verify failed'); }
    finally { setVerify(null); }
  };

  const setPrimary = async (d) => {
    try {
      const r = await axios.post(`${API}/builder/domains/${d.id}/primary`, {}, { headers: headers() });
      setItems((arr) => arr.map((x) => ({ ...x, is_primary: x.id === d.id }) || x));
      toast.success(`${d.host} is now the primary domain`);
      void r;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (d) => {
    if (!(await themedConfirm(`Disconnect ${d.host}?\n\nVisitors will no longer reach your site at this domain.`))) return;
    try {
      await axios.delete(`${API}/builder/domains/${d.id}`, { headers: headers() });
      setItems((arr) => arr.filter((x) => x.id !== d.id));
      toast.success('Domain disconnected');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-10 max-w-4xl" data-testid="domains-page">
      <div className="flex items-start justify-between gap-4 mb-7 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2">Settings · Domains</p>
          <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Custom domains</h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">Point your own domain at your ENTREPRENATIVES site. Free SSL is auto-provisioned once DNS verifies.</p>
        </div>
        <button onClick={() => setOpenAdd(true)} className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-2 hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)]" data-testid="domains-add">
          <Plus size={12} /> Connect domain
        </button>
      </div>

      {/* Show walkthrough — always accessible, even after first-time dismiss */}
      <button
        type="button"
        onClick={() => setOpenWizard(true)}
        className="mb-4 inline-flex items-center gap-1.5 text-[11px] text-white/55 hover:text-[#A78BFA] transition-colors"
        data-testid="domains-show-wizard"
      >
        <HelpCircle size={11} /> New to domains? Watch the 30-second walkthrough
      </button>

      <DomainWizardModal
        open={openWizard}
        onClose={() => setOpenWizard(false)}
        onConnect={() => setOpenAdd(true)}
      />

      {loading ? (
        <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-12 flex items-center justify-center gap-2 text-white/55">
          <Loader2 size={14} className="animate-spin" /> Loading domains…
        </div>
      ) : items.length === 0 ? (
        <>
          <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#A78BFA]/10 flex items-center justify-center mx-auto mb-4"><Globe size={22} className="text-[#A78BFA]" /></div>
            <h3 className="text-[16px] font-bold text-white font-syne">No custom domain yet</h3>
            <p className="text-[#9CA3AF] text-[12.5px] mt-1 max-w-md mx-auto">Connect a domain like <code className="text-violet-300">mystore.com</code> to give your site a professional URL. We&apos;ll auto-provision SSL once DNS verifies.</p>
            <button onClick={() => setOpenAdd(true)} className="mt-5 px-5 py-2 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="domains-empty-add">Connect your first domain</button>
            {platform.ip && (
              <p className="text-[10.5px] text-white/35 mt-6 font-mono">Platform IP for A records: {platform.ip}</p>
            )}
          </div>

          {/* Get-a-Domain — curated registrars for vendors without one yet */}
          <div className="mt-6 bg-[#15152E] border border-white/[0.06] rounded-2xl p-6" data-testid="get-a-domain-section">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div>
                <h3 className="text-[15px] font-bold text-white font-syne">Don&apos;t have a domain yet?</h3>
                <p className="text-[11.5px] text-[#A5A8C9] mt-1 max-w-lg">
                  We don&apos;t resell domains, so you always pay the wholesale rate. Pick a trusted registrar below, buy your domain, then come back and connect it.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { id: 'ionos',     label: 'IONOS',      tagline: '.com from $1 first year', bullets: ['Bulk pricing worldwide', 'Free WHOIS privacy', '24/7 phone support'], url: 'https://www.ionos.com/domains', accent: 'from-[#1F63BF] to-[#0C4CA8]' },
                { id: 'godaddy',   label: 'GoDaddy',    tagline: 'Widest TLD selection',     bullets: ['Huge extension catalog', 'Auto-renew reminders', 'Marketplace for premium names'], url: 'https://www.godaddy.com/domainsearch/find', accent: 'from-[#0A8E4C] to-[#046939]' },
                { id: 'domain-com',label: 'Domain.com', tagline: 'Simplest checkout',        bullets: ['Clean, up-front pricing', 'Included email forwarding', 'One-click DNS templates'], url: 'https://www.domain.com/', accent: 'from-[#A78BFA] to-[#6D28D9]' },
              ].map((r) => (
                <a
                  key={r.id} href={r.url} target="_blank" rel="noreferrer"
                  className="group block rounded-xl overflow-hidden border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#A78BFA]/50 transition-all"
                  data-testid={`registrar-${r.id}`}
                >
                  <div className={`h-1.5 w-full bg-gradient-to-r ${r.accent}`} />
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[14px] font-bold text-white tracking-tight">{r.label}</span>
                      <ExternalLink size={12} className="text-white/40 group-hover:text-white transition-colors" />
                    </div>
                    <p className="mt-1 text-[10.5px] text-[#A78BFA] font-semibold">{r.tagline}</p>
                    <ul className="mt-3 space-y-1">
                      {r.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-1.5 text-[11.5px] text-white/70">
                          <CheckCircle2 size={10} className="text-emerald-400 mt-0.5 shrink-0" /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <DomainCard key={d.id} d={d} onVerify={verify} onPrimary={setPrimary} onDelete={remove} verifying={verifying} />
          ))}
        </div>
      )}

      <AddDomainModal open={openAdd} onClose={() => setOpenAdd(false)} onAdded={(row) => setItems((arr) => [row, ...arr])} />
    </div>
  );
}
